import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { LuArrowLeft, LuLogIn, LuMail, LuMailCheck, LuSend } from 'react-icons/lu';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AuthField, AuthShell } from '../components/auth/AuthShell';
import { Button, ButtonLook, IconMedallion } from '../design-system/components';
import { useAuthStore } from '../stores/auth.store';
import { fireAndForget } from '../utils/fireAndForget';
import { withRedirect } from '../utils/safe-redirect';

interface ForgotPasswordFormData {
  email: string;
}

/**
 * Forgot Password Component
 * Allows users to request a password reset link
 */
function ForgotPassword() {
  // Navigation hook
  const navigate = useNavigate();
  // Back to Sign In keeps the ?redirect the member arrived with (fix round A5),
  // as the Sign In ↔ Sign Up links do.
  const [searchParams] = useSearchParams();
  const loginHref = withRedirect('/login', searchParams.get('redirect'));

  // Form state management
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  // React Hook Form
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors, isValid },
  } = useForm<ForgotPasswordFormData>({ mode: 'all' });

  // Auth store actions and state
  const forgotPassword = useAuthStore((state) => state.forgotPassword);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isAuthLoading = useAuthStore((state) => state.isLoading);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !isAuthLoading) {
      void navigate('/', { replace: true });
    }
  }, [isAuthenticated, isAuthLoading, navigate]);

  /**
   * Handle form submission
   * Sends forgot password request
   */
  async function submitForgotPasswordForm(postData: ForgotPasswordFormData) {
    setIsLoading(true);

    try {
      const { data, error, message } = await forgotPassword(postData.email);
      if (error) {
        toast.error(error);
      } else {
        setIsSuccess(true);
        toast.success(message || 'Password reset link sent to your email!');

        /// For development only, show the reset URL
        if (import.meta.env.DEV && data && typeof data === 'object' && 'reset_url' in data) {
          console.log('Password reset URL:', (data as { reset_url: string }).reset_url);
          toast.info('Check console for reset link (development only)', { autoClose: 5000 });
        }
      }
    } catch {
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  }

  if (isSuccess) {
    return (
      <AuthShell
        title="Check Your"
        accent="Email"
        lead={<IconMedallion icon={LuMailCheck} size="lg" />}
        tagline={
          <>
            We&apos;ve sent a password reset link to <strong>{getValues('email')}</strong>
          </>
        }
        footer={
          <p className="ee-auth-terms">
            Didn&apos;t receive the email? Check your spam folder or try again
          </p>
        }
      >
        {/* Instructions */}
        <ol className="ee-auth-steps">
          <li className="ee-auth-steps__item">
            <span className="ee-auth-steps__num" aria-hidden="true">
              1
            </span>
            <span>Check your email inbox (and spam folder)</span>
          </li>
          <li className="ee-auth-steps__item">
            <span className="ee-auth-steps__num" aria-hidden="true">
              2
            </span>
            <span>Click the reset link in the email</span>
          </li>
          <li className="ee-auth-steps__item">
            <span className="ee-auth-steps__num" aria-hidden="true">
              3
            </span>
            <span>Create your new password</span>
          </li>
        </ol>

        {/* Action Buttons */}
        <div className="ee-auth-actions">
          <Button
            variant="secondary"
            fullWidth
            onClick={() => {
              setIsSuccess(false);
              setValue('email', '');
            }}
            leadingIcon={<LuArrowLeft />}
          >
            Try Different Email
          </Button>

          {/* A router Link in the Button's look (ButtonLook, so it cannot
              drift from Button): a plain <a> would reload the app. */}
          <Link to={loginHref} className="ee-auth-buttonlink">
            <ButtonLook variant="primary" fullWidth leadingIcon={<LuLogIn />}>
              Back to Login
            </ButtonLook>
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Forgot"
      accent="Password?"
      tagline="No worries! Enter your email and we'll send you a reset link"
      footer={
        <p className="ee-auth-terms">
          Remember your password?{' '}
          <Link to={loginHref} className="ee-auth-link">
            Sign in
          </Link>
        </p>
      }
    >
      <form
        className="ee-auth-form"
        onSubmit={fireAndForget(handleSubmit(submitForgotPasswordForm))}
      >
        <AuthField id="email" label="Email Address" icon={LuMail} error={errors?.email?.message}>
          <input
            type="email"
            id="email"
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i,
                message: 'Enter a valid email',
              },
            })}
            placeholder="Enter your email address"
            required
          />
        </AuthField>

        {/* Submit Button */}
        <Button
          type="submit"
          fullWidth
          disabled={!isValid}
          loading={isLoading}
          leadingIcon={<LuSend />}
        >
          {isLoading ? 'Sending Reset Link...' : 'Send Reset Link'}
        </Button>
      </form>

      {/* Back to Login Link */}
      <p className="ee-auth-switch">
        <Link className="ee-auth-link ee-auth-back" to={loginHref}>
          <LuArrowLeft aria-hidden="true" />
          Back to Login
        </Link>
      </p>
    </AuthShell>
  );
}

export default ForgotPassword;
